import { useEffect, useState } from 'react';

import { validatePaymentLinkV2 } from '../../../../services/backendPaymentsV2Services';

import { captureException } from '../helpers/captureException';
import {
  createEmptyPaymentState,
  mapPaymentLinkResponse,
} from '../helpers/mapPaymentLinkResponse';
import { IPaymentLinkFormValues, IPaymentState } from '../types/types';

const LOAD_ERROR_MESSAGE =
  'Não foi possível carregar as informações do pagamento.';

interface UsePaymentLinkDataParams {
  link: string;
  reportError: (message: string | null) => void;
}

export const usePaymentLinkData = ({
  link,
  reportError,
}: UsePaymentLinkDataParams) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [paymentState, setPaymentState] = useState<IPaymentState>(() =>
    createEmptyPaymentState(link),
  );
  const [formValues, setFormValues] = useState<IPaymentLinkFormValues>();

  useEffect(() => {
    let isCurrent = true;

    const validateLink = async () => {
      try {
        setIsValidating(true);
        reportError(null);

        const { data } = await validatePaymentLinkV2(link);

        if (!isCurrent) return;

        const mapped = mapPaymentLinkResponse(link, data.response);

        setIsExpired(mapped.isExpired);
        setPaymentState(mapped.paymentState);
        setFormValues(mapped.formValues);
      } catch (error) {
        if (!isCurrent) return;

        captureException('link_validation', error);

        reportError(LOAD_ERROR_MESSAGE);
      } finally {
        if (isCurrent) setIsValidating(false);
      }
    };

    validateLink();

    return () => {
      isCurrent = false;
    };

  }, [link, reportError]);

  const isAlreadyPaid = Boolean(paymentState.paidAt && paymentState.submittedAt);

  return {
    isValidating,
    isExpired,
    isAlreadyPaid,
    paymentState,
    formValues,
  };
};

export default usePaymentLinkData;
